// ============================================================================
// bom-export — Stückliste/BOM-Aufbereitung für Angebots-/Bestellliste
// ----------------------------------------------------------------------------
// Reine Funktionen (kein React, kein DOM) → in tests/unit prüfbar.
// Wandelt die API-BOM (BillOfMaterials) in:
//   • CSV (de-AT: Semikolon-getrennt, Komma als Dezimaltrenner)  → Excel-AT-fest
//   • aufbereitete Anzeige-Zeilen (deutsche Kategorie-Labels)    → Druckblatt/Tabelle
//
// Warum Semikolon: de-AT-Excel erwartet ";" als Spaltentrenner, weil "," der
// Dezimaltrenner ist. Ein komma-getrenntes CSV mit Komma-Dezimalzahlen würde in
// AT-Excel die Spalten zerreißen. Daher Semikolon + Komma-Dezimal.
//
// Sicherheitsinvariante: reiner lokaler Export, kein Webshop-/Bestell-Call.
// Preise sind Netto-Richtwerte (Phase 0), keine Verbindlichkeit.
// ============================================================================

export interface BomLine {
  position: number;
  category: string;
  description: string;
  quantity: number;
  unit: string;
  unitPriceEurNet?: number;
  totalEurNet?: number;
}

export interface BillOfMaterials {
  projectId: string;
  generatedAt: string;
  systemSizeKwp: number;
  lines: BomLine[];
  totalEurNet: number;
  assumptions: string[];
}

/** Menschliche de-AT-Labels für die Engine-Kategorie-Codes. */
const CATEGORY_LABELS: Record<string, string> = {
  pv_module: "PV-Module",
  inverter: "Wechselrichter",
  mounting: "Montagesystem",
  cable: "Kabel/Verkabelung",
  protection: "Schutztechnik",
  storage: "Speicher",
};

/** Liefert ein lesbares Kategorie-Label; unbekannte Codes werden 1:1 durchgereicht. */
export function categoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? category;
}

/** de-AT-Zahl mit fixen Nachkommastellen (Komma als Dezimaltrenner). */
function deAtNumber(value: number, digits = 2): string {
  return value.toLocaleString("de-AT", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/**
 * CSV-Feld escapen: enthält das Feld ";", Anführungszeichen oder Zeilenumbruch,
 * wird es in doppelte Anführungszeichen gesetzt und enthaltene " verdoppelt.
 */
function csvCell(value: string): string {
  if (/[";\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Erzeugt das CSV für die Angebots-/Bestellliste.
 * Spalten (Pflicht laut Anforderung, in dieser Reihenfolge):
 *   Pos; Artikel; Menge; Einheit
 * plus kaufmännische Zusatzspalten (Einzelpreis netto, Gesamt netto) als
 * Komma-Dezimalzahlen. Trennzeichen: Semikolon (de-AT/Excel-AT).
 * Optional eine Summenzeile am Ende.
 */
export function bomToCsv(
  bom: Pick<BillOfMaterials, "lines" | "totalEurNet">,
  options: { includeTotalRow?: boolean } = {},
): string {
  const { includeTotalRow = true } = options;
  const sep = ";";
  const header = ["Pos", "Artikel", "Menge", "Einheit", "Einzelpreis netto (EUR)", "Gesamt netto (EUR)"];

  const lines: string[] = [header.map(csvCell).join(sep)];

  for (const line of bom.lines) {
    const artikel = `${categoryLabel(line.category)} — ${line.description}`;
    const cells = [
      String(line.position),
      artikel,
      deAtNumber(line.quantity, 0),
      line.unit,
      line.unitPriceEurNet != null ? deAtNumber(line.unitPriceEurNet, 2) : "",
      line.totalEurNet != null ? deAtNumber(line.totalEurNet, 2) : "",
    ];
    lines.push(cells.map(csvCell).join(sep));
  }

  if (includeTotalRow) {
    const totalCells = ["", "Gesamt netto", "", "", "", deAtNumber(bom.totalEurNet, 2)];
    lines.push(totalCells.map(csvCell).join(sep));
  }

  // CRLF-Zeilenenden für maximale Excel-Kompatibilität.
  return lines.join("\r\n");
}

/** Anzeige-/Druck-Zeile: Engine-Zeile + lesbares Kategorie-Label. */
export interface BomDisplayLine extends BomLine {
  categoryLabel: string;
}

/** Reichert die BOM-Zeilen mit deutschen Kategorie-Labels an (für Tabelle/Druckblatt). */
export function bomDisplayLines(bom: Pick<BillOfMaterials, "lines">): BomDisplayLine[] {
  return bom.lines.map((line) => ({ ...line, categoryLabel: categoryLabel(line.category) }));
}

/** Vorschlag für den CSV-Dateinamen (de-AT, projektbezogen, dateisicher). */
export function bomCsvFilename(projectId: string): string {
  const safe = projectId.replace(/[^a-zA-Z0-9_-]/g, "").slice(0, 24) || "projekt";
  return `stueckliste-${safe}.csv`;
}
